import { create } from 'zustand';
import ServiceCessao from '@/services/ServiceCessao';
import { notify } from '@/stores/useToast';
import UtilsMask from '@/utils/UtilsMask';
import {
  buildChartVM,
  buildRequestBody,
  SITUACAO_LABEL,
  type CessaoFiltros,
  type ChartVM,
  type DashboardDTO,
} from '@/lib/cessao';

export { SITUACAO_LABEL } from '@/lib/cessao';

// CON-5188: cada coluna ordenável da tabela mapeia para o alias que o backend aceita
// (history/cessions?sort=<alias>,<asc|desc>). Só as colunas com alias entram como
// sortable — as demais não têm suporte de ordenação no BFF.
const SORT_ALIASES: Record<string, string> = {
  origem: 'empresaOrigem',
  destino: 'empresaDestino',
  cpf: 'cpf',
  matricula: 'matricula',
  nome: 'nome',
  tpRegPrev: 'tpRegPrev',
  situacao: 'situacao',
  inicio: 'dataInicioCessao',
  fim: 'dataTerminoCessao',
  remuneracao: 'remuneracao',
};

export interface LinhaCessao {
  [k: string]: unknown;
  _key: string;
  empresaOrigem: string;
  cnpjOrigem: string;
  empresaDestino: string | null;
  cnpjDestino: string | null;
  cpf: string;
  matricula: string;
  nome: string;
  nomeSocial: string;
  tpRegPrev: string;
  situacao: string;
  inicio: string | null;
  fim: string | null;
  remuneracao: string | null;
}

export interface OpcoesFiltro {
  origem: { value: string; label: string }[];
  destino: { value: string; label: string }[];
  regime: { value: string; label: string }[];
}

export interface Contagem {
  cededOnCurrentDate: number;
  ceded: number;
  notCeded: number;
}

const FILTROS_VAZIO: CessaoFiltros = {
  originCnpjList: [],
  destinationCnpjList: [],
  tpRegPrevList: [],
  startDate: undefined,
  endDate: undefined,
  originRemunDeclarationToggle: null,
  isCeded: null,
};

const OPCOES_VAZIO: OpcoesFiltro = { origem: [], destino: [], regime: [] };

const CONTAGEM_VAZIA: Contagem = { cededOnCurrentDate: 0, ceded: 0, notCeded: 0 };

type Any = any;

const normalizar = (h: Any, i: number): LinhaCessao => ({
  _key: `${h.cpf ?? 'row'}-${h.matricula ?? ''}-${i}`,
  empresaOrigem: h.empresaOrigem ?? '',
  cnpjOrigem: UtilsMask.cnpj(h.cnpjOrigem),
  empresaDestino: h.empresaDestino ?? null,
  cnpjDestino: h.cnpjDestino ? UtilsMask.cnpj(h.cnpjDestino) : null,
  cpf: UtilsMask.cpf(h.cpf),
  matricula: h.matricula ?? '',
  nome: h.nome ?? '',
  nomeSocial: h.nomeSocialTrabalhador ?? '',
  tpRegPrev: h.tpRegPrev ?? '',
  situacao: SITUACAO_LABEL[h.situacao as string] ?? h.situacao ?? '',
  inicio: h.dataInicioCessao ?? null,
  fim: h.dataTerminoCessao ?? null,
  remuneracao: h.remuneracao ?? null,
});

interface State {
  dashboard: DashboardDTO | null;
  vm: ChartVM;
  lista: LinhaCessao[];
  total: number;
  page: number;
  size: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  busca: string;
  filtros: CessaoFiltros;
  opcoes: OpcoesFiltro;
  contagem: Contagem;
  cedidosNaDataCorrente: boolean;
  carregandoGraficos: boolean;
  carregandoLista: boolean;
  exportando: boolean;
  carregarDashboard: () => Promise<void>;
  carregarContagem: () => Promise<void>;
  carregarHistorico: () => Promise<void>;
  carregarFiltros: () => Promise<void>;
  aplicarFiltros: (f: CessaoFiltros) => Promise<void>;
  limparFiltros: () => Promise<void>;
  setToggleCedidosNaDataCorrente: (v: boolean) => Promise<void>;
  setBusca: (v: string) => void;
  buscar: () => Promise<void>;
  setPage: (page: number) => Promise<void>;
  setSize: (size: number) => Promise<void>;
  setSort: (by: string, order: 'asc' | 'desc') => Promise<void>;
  exportar: () => Promise<void>;
}

function _body(get: () => State) {
  const s = get();
  return buildRequestBody({
    busca: s.busca,
    filtros: s.filtros,
    isCededOnCurrentDate: s.cedidosNaDataCorrente,
  });
}

export const useCessaoStore = create<State>((set, get) => ({
  dashboard: null,
  vm: buildChartVM(undefined),
  lista: [],
  total: 0,
  page: 0,
  size: 50,
  sortBy: '',
  sortOrder: 'asc',
  busca: '',
  filtros: { ...FILTROS_VAZIO },
  opcoes: { ...OPCOES_VAZIO },
  contagem: { ...CONTAGEM_VAZIA },
  cedidosNaDataCorrente: false,
  carregandoGraficos: false,
  carregandoLista: false,
  exportando: false,

  async carregarDashboard() {
    set({ carregandoGraficos: true });
    try {
      const { data } = await ServiceCessao.dashboard(_body(get));
      set({ dashboard: data, vm: buildChartVM(data) });
    } catch {
      set({ dashboard: null, vm: buildChartVM(undefined) });
      notify.error('Erro ao carregar os gráficos de cessão.');
    } finally {
      set({ carregandoGraficos: false });
    }
  },

  async carregarContagem() {
    try {
      const { data } = await ServiceCessao.count(_body(get));
      set({
        contagem: {
          cededOnCurrentDate: data?.cededOnCurrentDate ?? 0,
          ceded: data?.ceded ?? 0,
          notCeded: data?.notCeded ?? 0,
        },
      });
    } catch {
      set({ contagem: { ...CONTAGEM_VAZIA } });
    }
  },

  async carregarHistorico() {
    const { page, size, sortBy, sortOrder } = get();
    set({ carregandoLista: true });
    try {
      const alias = SORT_ALIASES[sortBy];
      const { data } = await ServiceCessao.history(_body(get), {
        page,
        size,
        sort: alias ? `${alias},${sortOrder}` : undefined,
      });
      set({
        lista: (data.content ?? []).map(normalizar),
        total: data.totalElements ?? 0,
      });
    } catch {
      set({ lista: [], total: 0 });
      notify.error('Erro ao carregar as cessões.');
    } finally {
      set({ carregandoLista: false });
    }
  },

  async carregarFiltros() {
    try {
      const { data } = await ServiceCessao.filters();
      const opt = (arr: Any[]) => (arr ?? []).map((i) => ({ value: i.itemId, label: i.itemTitle }));
      set({
        opcoes: {
          origem: opt(data.groupOriginCnpjList),
          destino: opt(data.groupDestinationCnpjList),
          regime: opt(data.groupRegimesPrevidenciarios),
        },
      });
    } catch {
      notify.error('Erro ao carregar as opções de filtro.');
    }
  },

  async aplicarFiltros(f) {
    // Aplicar filtros desliga o toggle "Cedidos na data corrente".
    set({ filtros: f, cedidosNaDataCorrente: false, page: 0 });
    await Promise.all([
      get().carregarHistorico(),
      get().carregarDashboard(),
      get().carregarContagem(),
    ]);
  },

  async limparFiltros() {
    set({ filtros: { ...FILTROS_VAZIO }, cedidosNaDataCorrente: false, page: 0 });
    await Promise.all([
      get().carregarHistorico(),
      get().carregarDashboard(),
      get().carregarContagem(),
    ]);
  },

  async setToggleCedidosNaDataCorrente(v) {
    // Ligar o toggle limpa os demais filtros (buildRequestBody também zera o corpo).
    set({ cedidosNaDataCorrente: v, filtros: { ...FILTROS_VAZIO }, page: 0 });
    await Promise.all([
      get().carregarHistorico(),
      get().carregarDashboard(),
      get().carregarContagem(),
    ]);
  },

  setBusca(v) {
    set({ busca: v });
  },

  async buscar() {
    set({ page: 0 });
    await get().carregarHistorico();
  },

  async setPage(page) {
    set({ page });
    await get().carregarHistorico();
  },

  async setSize(size) {
    set({ size, page: 0 });
    await get().carregarHistorico();
  },

  async setSort(by, order) {
    set({ sortBy: by, sortOrder: order, page: 0 });
    await get().carregarHistorico();
  },

  async exportar() {
    set({ exportando: true });
    try {
      await ServiceCessao.exportData(_body(get));
      notify.success('Exportação em processamento. Acompanhe em Arquivos › Exportações.');
    } catch {
      notify.error('Não foi possível exportar as cessões.');
    } finally {
      set({ exportando: false });
    }
  },
}));
